/**
 * DataTable - Generic table component with sorting and selection
 * Supports responsive layout (table on desktop, cards on mobile)
 */

import { ReactNode, useRef, useEffect } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (item: T) => ReactNode;
  width?: string; // Tailwind class (e.g., 'w-1/3')
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  selectedIds: Set<string>;
  onSelectChange: (id: string) => void;
  onSelectAll: (allIds: string[]) => void;
  onSort?: (key: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  idKey: keyof T;
  loading?: boolean;
  emptyMessage?: string;
  selectable?: boolean;
}

/**
 * Generic data table with sorting and selection
 */
export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  selectedIds,
  onSelectChange,
  onSelectAll,
  onSort,
  sortBy,
  sortOrder,
  idKey,
  loading = false,
  emptyMessage = 'No items found',
  selectable = true,
}: DataTableProps<T>): JSX.Element {
  const allIds = data.map((item) => String(item[idKey]));
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id)) && !allSelected;

  // Use ref + useEffect for indeterminate state (avoids ref callback during render)
  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  function handleSort(key: string): void {
    if (onSort) {
      onSort(key);
    }
  }

  function getSortIcon(columnKey: string): ReactNode {
    if (sortBy !== columnKey) {
      return (
        <span className="text-gray-400 dark:text-gray-600">
          ↕
        </span>
      );
    }

    return sortOrder === 'asc' ? (
      <span className="text-primary-600 dark:text-primary-400">↑</span>
    ) : (
      <span className="text-primary-600 dark:text-primary-400">↓</span>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table view */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {/* Checkbox column */}
              {selectable && (
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    ref={checkboxRef}
                    checked={allSelected}
                    onChange={() => onSelectAll(allIds)}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}

              {/* Column headers */}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider ${column.width || ''}`}
                >
                  {column.sortable ? (
                    <button
                      onClick={() => handleSort(column.key)}
                      className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                    >
                      {column.header}
                      {getSortIcon(column.key)}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((item) => {
              const itemId = String(item[idKey]);
              const isSelected = selectedIds.has(itemId);

              return (
                <tr
                  key={itemId}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  {/* Checkbox cell */}
                  {selectable && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onSelectChange(itemId)}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                  )}

                  {/* Data cells */}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100"
                    >
                      {column.render
                        ? column.render(item)
                        : String(item[column.key] || '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-4">
        {data.map((item) => {
          const itemId = String(item[idKey]);
          const isSelected = selectedIds.has(itemId);

          return (
            <div
              key={itemId}
              className={`card p-4 ${
                isSelected ? 'ring-2 ring-primary-500 dark:ring-primary-400' : ''
              }`}
            >
              {/* Checkbox */}
              {selectable && (
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectChange(itemId)}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      Select
                    </span>
                  </label>
                </div>
              )}

              {/* Card content */}
              <div className="space-y-2">
                {columns.map((column) => (
                  <div key={column.key} className="flex justify-between items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {column.header}:
                    </span>
                    <span className="text-sm text-gray-900 dark:text-gray-100 text-right">
                      {column.render
                        ? column.render(item)
                        : String(item[column.key] || '')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
